import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import UnifiedNavigation from '../../components/Navigation/UnifiedNavigation';
import Footer from '../../components/Footer/Footer';
import InstagramSection from '../../components/BrandHomePage/InstagramSection';
import { useOverlayManager } from '../../contexts/OverlayContext';
import { initializeAllFeatures, initializeDragScroll } from '../../utils/interactiveFeatures';
import { images } from '../../components/shared/assets';
import { HandDrawnButton } from '../../components/shared/HandDrawnButton/HandDrawnButton';
import { createSelfbookHandler } from '../../utils/selfbook';
import '../../styles/property-pages.css';
import './AccommodationsDetailAtacama.css';

const AccommodationsDetailAtacama: React.FC = () => {
  const { roomType } = useParams<{ roomType: string }>();
  const navigate = useNavigate();
  const { setHoveredDestination } = useOverlayManager();
  const handleBooking = createSelfbookHandler(navigate, '/atacama/contact/');

  useEffect(() => {
    initializeAllFeatures();
    const carouselCleanup = initializeDragScroll('accommodation-detail-carousel');
    setHoveredDestination(null);

    return () => {
      if (typeof carouselCleanup === 'function') carouselCleanup();
    };
  }, []);

  // Room data based on type - Using exact approved copy from property map
  const roomData = {
    'superior-round-suites': {
      title: 'Superior Round Suites',
      subtitle: '65 sq m / 800 sq ft',
      description: 'This luxurious round room is built of stone, adobe and wood, and has a thatched roof. The ample space is designed for relaxation, privacy and well-deserved rest.',
      features: [
        'Private patio with outdoor shower',
        'Bathroom with bathtub and shower',
        'Walk-in wardrobe, desk and chaise lounge',
        'Measuring 65 sq m / 800 sq ft, including patio'
      ],
      additionalInfo: 'Large shared terrace with patio furniture.',
      youMayLike: {
        title: 'Round Rooms',
        description: 'Private patio with outdoor shower. Bathroom with shower. Measuring 50 sq m / 600 sq ft, including patio.'
      },
      images: [images.villas13, images.masterVilla, images.video1, images.video2]
    },
    'round-rooms': {
      title: 'Round Rooms',
      subtitle: '50 sq m / 600 sq ft',
      description: 'This luxurious round room is built of stone, adobe and wood, and has a thatched roof. The ample space is designed for relaxation, privacy and well-deserved rest.',
      features: [
        'Private patio with outdoor shower',
        'Bathroom with shower',
        'Measuring 50 sq m / 600 sq ft, including patio'
      ],
      additionalInfo: 'Large shared terrace with patio furniture.',
      youMayLike: {
        title: 'Superior Round Suites',
        description: 'Private patio with outdoor shower. Bathroom with bathtub and shower. Walk-in wardrobe, desk and chaise lounge. Measuring 65 sq m / 800 sq ft, including patio.'
      },
      images: [images.masterVilla, images.villas13, images.video3, images.video4]
    }
  };

  const currentRoom = roomData[roomType as keyof typeof roomData] || roomData['superior-round-suites'];

  const handleBackClick = () => {
    navigate('/atacama/accommodations/');
  };

  return (
    <div className="property-page accommodation-detail-atacama">
      {/* Navigation - Atacama variant */}
      <UnifiedNavigation variant="light" destination="atacama" />

      {/* Horizontal Scrolling Carousel */}
      <section className="accommodation-detail-carousel-container">
        <div
          className="accommodation-detail-carousel"
          id="accommodation-detail-carousel"
        >
          {/* Section 1: Main Diptych - Hero content */}
          <div className="detail-section main-diptych">
            <div className="section-content">
              <div className="text-content">
                <button className="back-button" onClick={handleBackClick}>
                  <img src="https://images.ctfassets.net/hz1mviiqoqof/2zR3YIhzH74vxN63GI2WIu/6629b5528d3c30ede8919f4629380aa8/carouselArrow.svg" alt="Back" />
                  <span>BACK</span>
                </button>
                <h1>{currentRoom.title}</h1>
                <h2>{currentRoom.subtitle}</h2>
                <p>{currentRoom.description}</p>
                <HandDrawnButton
                  text="BOOK NOW"
                  onClick={handleBooking}
                  variant="atacama"
                  color="awasi"
                  animation="stroke-to-fill"
                  size="wide"
                  className="accommodation-book-button"
                />
              </div>
              <div className="hero-image">
                <img src={currentRoom.images[0]} alt={currentRoom.title} />
              </div>
            </div>
          </div>

          {/* Section 2: Checklist + Small Image */}
          <div className="detail-section checklist-section">
            <div className="section-content">
              <div className="features-content">
                <h3>Room Features</h3>
                <ul className="features-list">
                  {currentRoom.features.map((feature, index) => (
                    <li key={index}>{feature}</li>
                  ))}
                </ul>
              </div>
              <div className="small-image">
                <img src={currentRoom.images[1]} alt="Room feature" />
              </div>
            </div>
          </div>

          {/* Section 3: Large Image */}
          <div className="detail-section large-image-section">
            <img src={currentRoom.images[2]} alt="Room panoramic view" />
          </div>

          {/* Section 4: Scattered Collage */}
          <div className="detail-section scattered-collage">
            <div className="collage-container">
              <div className="collage-image image-1">
                <img src={images.video1} alt="Bathroom view" />
              </div>
              <div className="collage-image image-2">
                <img src={images.video2} alt="Patio area" />
              </div>
              <div className="collage-image image-3">
                <img src={images.video3} alt="Interior detail" />
              </div>
              <div className="collage-image image-4">
                <img src={images.video4} alt="Desert view" />
              </div>
            </div>
          </div>

          {/* Section 5: Another Large Image */}
          <div className="detail-section large-image-section">
            <img src={images.producingNaturePage.fullDesertLandscape} alt="Desert landscape" />
          </div>

          {/* Section 6: Floorplan Section */}
          <div className="detail-section floorplan-section">
            <div className="section-content">
              <div className="floorplan-content">
                <h3>Room Layout</h3>
                <div className="floorplan-image">
                  <img src={images.mapRectangle8767} alt="Room floorplan" />
                </div>
                <div className="floorplan-details">
                  <div className="detail-item">
                    <h4>Total Area</h4>
                    <p>{roomType === 'superior-round-suites' ? '65 sq m / 800 sq ft' : '50 sq m / 600 sq ft'}</p>
                  </div>
                  <div className="detail-item">
                    <h4>Layout</h4>
                    <p>Open-plan design with separate bathroom and patio areas</p>
                  </div>
                  <div className="detail-item">
                    <h4>Orientation</h4>
                    <p>Strategic positioning for optimal desert views and privacy</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Section 7: Full Image */}
          <div className="detail-section large-image-section">
            <img src={currentRoom.images[3]} alt="Evening view" />
          </div>

          {/* Section 8: Triptych */}
          <div className="detail-section triptych-section">
            <div className="triptych-container">
              <div className="triptych-image">
                <img src={images.film13} alt="Morning light" />
              </div>
              <div className="triptych-image">
                <img src={images.film14} alt="Desert detail" />
              </div>
              <div className="triptych-image">
                <img src={images.film15} alt="Evening atmosphere" />
              </div>
            </div>
          </div>

          {/* Section 9: You May Also Like */}
          <div className="detail-section you-may-like-section">
            <div className="section-content">
              <h3>You May Also Like</h3>
              <div className="related-rooms">
                {roomType === 'superior-round-suites' ? (
                  <div className="related-room-card">
                    <img src={images.masterVilla} alt="Round Rooms" />
                    <div className="room-info">
                      <h4>Round Rooms</h4>
                      <p>Private patio with outdoor shower. Bathroom with shower. Measuring 50 sq m / 600 sq ft, including patio.</p>
                      <HandDrawnButton
                        text="SEE DETAILS"
                        onClick={() => navigate('/atacama/accommodations/round-rooms/')}
                        variant="atacama"
                        color="awasi"
                        animation="stroke-to-stroke"
                        size="normal"
                        className="accommodation-details-button"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="related-room-card">
                    <img src={images.villas13} alt="Superior Round Suites" />
                    <div className="room-info">
                      <h4>Superior Round Suites</h4>
                      <p>Private patio with outdoor shower. Bathroom with bathtub and shower. Walk-in wardrobe, desk and chaise lounge. Measuring 65 sq m / 800 sq ft, including patio.</p>
                      <HandDrawnButton
                        text="SEE DETAILS"
                        onClick={() => navigate('/atacama/accommodations/superior-round-suites/')}
                        variant="atacama"
                        color="awasi"
                        animation="stroke-to-stroke"
                        size="normal"
                        className="accommodation-details-button"
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="booking-section">
                <h4>Ready to Experience the Atacama?</h4>
                <p>Book your stay and discover the world's driest desert in ultimate comfort</p>
                <HandDrawnButton
                  text="BOOK NOW"
                  onClick={handleBooking}
                  variant="atacama"
                  color="awasi"
                  animation="stroke-to-fill"
                  size="wide"
                  className="accommodation-book-large-button"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Instagram Section */}
      <InstagramSection
        posts={[
          { src: images.film13, alt: "Instagram 1" },
          { src: images.filmVideo1, alt: "Instagram 2", isVideo: true },
          { src: images.film14, alt: "Instagram 3" },
          { src: images.film15, alt: "Instagram 4" }
        ]}
      />

      {/* Footer */}
      <Footer variant="atacama" />
    </div>
  );
};

export default AccommodationsDetailAtacama;